import {
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Tabs,
  Tag,
  Typography
} from 'antd'
import { Bot, Settings } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ContentSpinner, DateTimeCell } from 'src/components'
import { useDocumentTitle, useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router/paths'
import { WorkspacePermissionCode } from 'src/utils'

import {
  AgentActivityTab,
  AgentApprovalsTab,
  AgentExternalToolsTab,
  AgentMcpTab,
  AgentOverviewTab,
  AgentToolsTab,
  useWorkspaceAgent
} from '../components/agents'
import { parseAllowedScopes } from '../components/agents/scopeModel'
import { TIER_COLOR, TIER_LABEL_KEY, tierOf } from '../components/agents/tier'
import { useAgentConnections } from '../components/agents/useAgentConnections'

import { WorkspacePageShell, useWorkspaceRoute } from './WorkspacePageShell'

const { Text, Title } = Typography

type TabKey =
  | 'overview'
  | 'mcp'
  | 'connections'
  | 'external-tools'
  | 'approvals'
  | 'activity'

export const WorkspaceAgentDetailPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id, workspaceId } = useWorkspaceRoute()
  const { agentId = '' } = useParams<{ agentId: string }>()

  const [tab, setTab] = useState<TabKey>('overview')

  const {
    data: agent,
    isLoading,
    isFetching
  } = useWorkspaceAgent(workspaceId, agentId)

  useDocumentTitle(agent?.name)

  const mayManageAgents = useHasWorkspacePermissions(
    WorkspacePermissionCode.APPLICATIONS_MANAGE
  )

  // Scopes are per connection now — the hero count is the distinct codes granted
  // across all of this agent's MCP connections.
  const { connections } = useAgentConnections(workspaceId, agentId)

  if (isLoading || (isFetching && !agent)) {
    return (
      <WorkspacePageShell breadcrumbLeaf={t('workspace.agents.title')}>
        <ContentSpinner minHeight={280} />
      </WorkspacePageShell>
    )
  }

  if (!agent) {
    return (
      <WorkspacePageShell
        breadcrumbLeaf={t('workspace.agents.detail.notFound')}
      >
        <Card>
          <Empty description={t('workspace.agents.detail.notFound')} />
        </Card>
      </WorkspacePageShell>
    )
  }

  const grantedCodes = new Set<string>()
  connections.forEach((c) =>
    c.links.forEach((rl) =>
      parseAllowedScopes(rl.link.allowed_scopes).forEach((code) =>
        grantedCodes.add(code)
      )
    )
  )
  const scopeCount = grantedCodes.size

  return (
    <WorkspacePageShell breadcrumbLeaf={agent.name}>
      <Flex vertical gap={14}>
        <Card>
          <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap">
            <Flex align="flex-start" gap={12}>
              <span
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: 'var(--ant-color-fill-secondary)',
                  color: 'var(--ant-color-text-secondary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto'
                }}
              >
                <Bot size={22} />
              </span>
              <div>
                <Flex align="center" gap={8} wrap="wrap">
                  <Title level={4} style={{ margin: 0 }}>
                    {agent.name}
                  </Title>
                  <Tag
                    color={TIER_COLOR[tierOf(agent)]}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {t(TIER_LABEL_KEY[tierOf(agent)], {
                      defaultValue: tierOf(agent)
                    })}
                  </Tag>
                  {agent.restricted ? (
                    <Tag style={{ marginInlineEnd: 0 }}>
                      {t('workspace.agents.restricted.label', {
                        defaultValue: 'Restricted'
                      })}
                    </Tag>
                  ) : null}
                </Flex>
                {agent.description ? (
                  <Text type="secondary">{agent.description}</Text>
                ) : null}
              </div>
            </Flex>
            {mayManageAgents ? (
              <Button
                icon={<Settings size={15} />}
                disabled={agent.canEdit === false}
                onClick={() =>
                  navigate(paths.workspaces.agentEdit(id, agent.id))
                }
              >
                {t('workspace.agents.actions.configure')}
              </Button>
            ) : null}
          </Flex>

          <Descriptions
            column={{ xs: 1, sm: 2, lg: 3 }}
            size="small"
            style={{ marginTop: 16 }}
          >
            <Descriptions.Item label={t('workspace.agents.detail.hero.scopes')}>
              {t('workspace.agents.detail.hero.scopesCount', {
                count: scopeCount
              })}
            </Descriptions.Item>
            {agent.model ? (
              <Descriptions.Item
                label={t('workspace.agents.detail.hero.model')}
              >
                {agent.model}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item
              label={t('workspace.agents.detail.hero.created')}
            >
              <DateTimeCell value={agent.createdAt} variant="date" />
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as TabKey)}
          tabBarStyle={{ marginBottom: 0 }}
          items={[
            {
              key: 'overview',
              label: t('workspace.agents.detail.tabs.overview')
            },
            {
              key: 'mcp',
              label: t('workspace.agents.detail.tabs.mcp')
            },
            {
              key: 'connections',
              label: t('workspace.agents.detail.tabs.connections')
            },
            {
              key: 'external-tools',
              label: t('workspace.agents.detail.tabs.externalTools')
            },
            {
              key: 'approvals',
              label: t('workspace.agents.detail.tabs.approvals')
            },
            {
              key: 'activity',
              label: t('workspace.agents.detail.tabs.activity')
            }
          ]}
        />

        {tab === 'overview' && (
          <AgentOverviewTab workspaceId={workspaceId} agent={agent} />
        )}
        {tab === 'mcp' && (
          <AgentMcpTab workspaceId={workspaceId} agent={agent} />
        )}
        {tab === 'connections' && (
          <AgentToolsTab workspaceId={workspaceId} agent={agent} />
        )}
        {tab === 'external-tools' && (
          <AgentExternalToolsTab workspaceId={workspaceId} agent={agent} />
        )}
        {tab === 'approvals' && (
          <AgentApprovalsTab workspaceId={workspaceId} agent={agent} />
        )}
        {tab === 'activity' && (
          <AgentActivityTab workspaceId={workspaceId} agent={agent} />
        )}
      </Flex>
    </WorkspacePageShell>
  )
}
